import { getExtension, parseReference } from "@medplum/core";
import type { Invoice, Organization, PaymentReconciliation } from "@medplum/fhirtypes";

import {
  isLedgerSource,
  LEDGER_SOURCE,
  LEDGER_SOURCE_OPTIONS,
  type LedgerSource,
} from "@/billing/constants/ledger-source";
import { SERVICE_PAYER_CODE } from "@/billing/constants/service-payer-code";
import { getInvoiceServicePayerCode } from "@/billing/utils/service-payer-code";
import { Extensions } from "@/fhir/enums/extensions";

export function getLedgerSource(invoice: Invoice | undefined): LedgerSource {
  const payer = invoice ? getInvoiceServicePayerCode(invoice) : undefined;
  if (payer === SERVICE_PAYER_CODE.Insurance) return LEDGER_SOURCE.Insurance;
  if (payer === SERVICE_PAYER_CODE.SelfPay) return LEDGER_SOURCE.Client;
  return LEDGER_SOURCE.Other;
}

/**
 * The Source choices for a payment on this statement. A self-pay statement is billed to the client,
 * so Insurance is not offered; every other statement keeps the full list.
 *
 * @param invoice The statement the payment is for, when one is selected.
 * @returns The Source dropdown options.
 */
export function getLedgerPaymentSourceOptions(
  invoice: Invoice | undefined,
): Array<{ value: LedgerSource; label: string }> {
  const isSelfPay = invoice && getInvoiceServicePayerCode(invoice) === SERVICE_PAYER_CODE.SelfPay;
  if (!isSelfPay) return [...LEDGER_SOURCE_OPTIONS];
  return LEDGER_SOURCE_OPTIONS.filter((option) => option.value !== LEDGER_SOURCE.Insurance);
}

/** The payer Organization id on an insurance statement, when `recipient` points at one. */
export function getInvoicePayerOrganizationId(invoice: Invoice): string | undefined {
  return organizationIdFromReference(invoice.recipient);
}

/** The payer Organization id on a posted payment, when `paymentIssuer` points at one. */
export function getPaymentIssuerOrganizationId(
  paymentReconciliation: PaymentReconciliation,
): string | undefined {
  return organizationIdFromReference(paymentReconciliation.paymentIssuer);
}

function organizationIdFromReference(
  reference: { reference?: string } | undefined,
): string | undefined {
  if (!reference?.reference?.startsWith("Organization/")) return undefined;
  const [, id] = parseReference(reference);
  return id;
}

/**
 * Source shown on a charge: the payer organization name for insurance, Self-Pay
 * for client-billed statements, otherwise Other.
 */
export function getLedgerSourceLabel(
  invoice: Invoice | undefined,
  organizationsById: Map<string, Organization>,
): string {
  const source = getLedgerSource(invoice);
  if (source !== LEDGER_SOURCE.Insurance || !invoice) return source;

  const organizationId = getInvoicePayerOrganizationId(invoice);
  const organizationName = organizationId
    ? organizationsById.get(organizationId)?.name?.trim()
    : undefined;
  const display = invoice.recipient?.display?.trim();

  return organizationName || display || LEDGER_SOURCE.Insurance;
}

/**
 * Source shown on a nested payment or adjustment. Ledger-posted entries store
 * Source on the PaymentReconciliation; remittance lines without that extension
 * keep the statement payer.
 */
export function getLedgerEntrySourceLabel(
  paymentReconciliation: PaymentReconciliation,
  organizationsById: Map<string, Organization>,
): string | undefined {
  const source = getExtension(paymentReconciliation, Extensions.LedgerSource)?.valueString?.trim();
  if (!isLedgerSource(source)) return undefined;
  if (source !== LEDGER_SOURCE.Insurance) return source;

  const issuer = paymentReconciliation.paymentIssuer;
  if (issuer?.reference?.startsWith("Organization/")) {
    const [, organizationId] = parseReference(issuer);
    const organizationName = organizationId
      ? organizationsById.get(organizationId)?.name?.trim()
      : undefined;
    if (organizationName) return organizationName;
  }
  const display = issuer?.display?.trim();
  return display || LEDGER_SOURCE.Insurance;
}
